/**
 * OpenAI-Ads-Pixel (cowork24.ch).
 *
 * Drittes Gegenstück zu `util/analytics.js` (GA4) neben `util/reddit.js`:
 * Dieselben semantischen Ereignisse werden zusätzlich an OpenAI gemeldet, damit
 * Kampagnen in ChatGPT auf echte Conversions optimieren können. Die Zuordnung
 * steht in `OPENAI_EVENTS` — jedes GA4-Ereignis wird genau einmal gemeldet.
 *
 * Geladen wird das Pixel erst nach `window` load + Idle-Phase (siehe
 * `util/DeferredAnalyticsScripts.js`), damit der Main-Thread beim First Paint
 * frei bleibt.
 *
 * **Strikte Validierung — der wichtigste Unterschied zu Reddit:** Das SDK prüft
 * jeden Aufruf gegen ein Schema und VERWIRFT das Ereignis bei jedem Verstoss
 * (unbekanntes Feld, fehlende `type`, `amount` ohne `currency`). Reddit meckert
 * nur in den Diagnostics und zählt trotzdem. Deshalb baut `toOpenAiProps` die
 * Nutzlast strikt aus der Allowlist des jeweiligen Datenobjekt-Typs auf statt
 * die GA4-Parameter durchzureichen.
 *
 * **Einwilligung:** Wie Reddit kennt OpenAI keinen Consent Mode — das SDK setzt
 * die Erstanbieter-Cookies `__obref` (Browser-Kennung, 365 Tage) und `__oppref`
 * (Klick-ID aus dem `oppref`-Query-Parameter). Im EWR/UK wird das Skript
 * deshalb ohne Zustimmung gar nicht erst eingebunden; ein nachträglicher
 * Widerspruch schaltet ein bereits geladenes SDK über `oaiq('consent', false)`
 * stumm (das ist mehr, als beim Reddit-Pixel möglich ist).
 *
 * Ohne `REACT_APP_OPENAI_PIXEL_ID` ist die gesamte Datei ein No-Op.
 */
import { openAiPixelId } from '../config/configAnalytics';
import { CONSENT_DENIED, CONSENT_GRANTED, isConsentRequiredRegion, readConsent } from './consent';

const SCRIPT_ID = 'deferred-openai-pixel-js';
const SCRIPT_SRC = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';

/**
 * Sentinel für Ereignisse ohne passendes OpenAI-Standardereignis: Sie gehen als
 * `custom` raus, wobei der `custom_event_name` exakt der GA4-Name ist. So bleibt
 * die Taxonomie zwischen GA4 und OpenAI 1:1 und lesbar im Events-Bericht.
 */
const CUSTOM = Symbol('openai-custom-event');

/**
 * GA4-Ereignis → OpenAI-Ereignis.
 *
 * Bewusst NICHT gemeldet: `login`, `cta_click` und `badge_tooltip_open` —
 * reine Interaktionssignale in hoher Frequenz, die im Events Manager nur die
 * Conversion-Auswahl zumüllen. `badge_tooltip_open` feuert schon beim blossen
 * Überfahren des Siegels, und eine Inseratsseite trägt zwei davon: ein Zeiger,
 * der die Seite überstreicht, erzeugt mehrere Ereignisse pro Besuch. Ein
 * bewusster Klick (`cta_click`) ist nach genau dieser Regel ausgeschlossen —
 * ein Hover erfüllt sie noch deutlicher.
 * hoher Frequenz, dieselbe Entscheidung wie bei Reddit.
 *
 * Zu `lead_created`: `generate_lead` ist die ANGEBOTSSEITE (Space-Betreiber) und
 * behält das Standardereignis. Die Nachfrageseite (`contact_host`) läuft
 * absichtlich als eigenes Custom-Ereignis — dieselbe Trennung wie in
 * GA4/Google Ads/Reddit, damit ein B2C-Lead nie die B2B-Lead-Optimierung
 * verwässert.
 */
const OPENAI_EVENTS = {
  // Hauptconversion: Buchungsanfrage (beide Flows — Standard-Checkout und
  // Soft-Booking — feuern `booking_request`, siehe CLAUDE.md). Das ist das
  // Ereignis, auf das das Pixel „Buchungsanfrage" ausgelegt ist.
  booking_request: 'order_created',
  subscription_start: 'subscription_created',
  sign_up: 'registration_completed',
  generate_lead: 'lead_created',
  view_item: 'contents_viewed',
  begin_checkout: 'checkout_started',
  // OpenAI kennt kein Standardereignis für eine Suche.
  search: CUSTOM,
  // Concierge-Suche: eigenes Custom-Ereignis neben `search`, damit die Nutzung
  // des Features getrennt sichtbar bleibt.
  ai_concierge_search: CUSTOM,
  // Absichtssignal der Angebotsseite. BEWUSST Custom und NICHT `lead_created`:
  // Ein Klick auf „Space inserieren" ist keine Anfrage. Würde er als Lead
  // zählen, lernte das Gebotsmodell, Knopfdrücker zu finden — derselbe Fehler,
  // der bei Google Ads schon einmal Schein-Conversions erzeugt hat (siehe
  // CLAUDE.md, „Registrierung (Seitenaufbau /signup)").
  list_space_intent: CUSTOM,
  contact_host: CUSTOM,
  listing_start: CUSTOM,
  listing_published: CUSTOM,
  // `booking_authorized` läuft im Standard-Checkout zusätzlich zu
  // `booking_request` — deshalb Custom statt eines zweiten `order_created`.
  booking_authorized: CUSTOM,
  booking_confirmed: CUSTOM,
  lounge_booking_click: CUSTOM,
  // Badge und Anbieter-Stupser: durchgehend custom.
  // Eine eingeblendete Flaeche, ein geoeffneter Tooltip und ein Klick auf einen
  // internen Hinweis sind KEINE Anfrage — als Lead gezaehlt lernte das
  // Gebotsmodell, Knopfdruecker zu finden (dieselbe Begruendung wie bei
  // list_space_intent). Ausserdem richten sich alle drei an ANBIETER, die schon
  // angemeldet sind: als Conversion gezaehlt wuerden sie die Akquise-Signale
  // verwaessern, die diese Kampagnen eigentlich suchen.
  banner_shown: CUSTOM,
  banner_cta_click: CUSTOM,
};

/**
 * OpenAI-Ereignis → Typ des Datenobjekts. Fest vom SDK vorgegeben; ein
 * abweichendes `data.type` lässt das SDK das Ereignis verwerfen
 * („eventProps.type should be …").
 */
const EVENT_DATA_TYPES = {
  page_viewed: 'contents',
  contents_viewed: 'contents',
  items_added: 'contents',
  checkout_started: 'contents',
  order_created: 'contents',
  lead_created: 'customer_action',
  registration_completed: 'customer_action',
  appointment_scheduled: 'customer_action',
  subscription_created: 'plan_enrollment',
  trial_started: 'plan_enrollment',
  custom: 'custom',
};

/**
 * Felder je Datenobjekt-Typ (Allowlist des SDK). `customer_action` kennt KEINE
 * `contents` — ein Inserat mitzuschicken würde `sign_up` und `generate_lead`
 * komplett verwerfen.
 */
const CONTENTS_DATA_TYPES = new Set(['contents', 'plan_enrollment', 'custom']);

/**
 * Ob das Pixel geladen und Ereignisse gemeldet werden dürfen.
 *
 * - Ohne konfigurierte Pixel-ID: nein.
 * - Mit gespeicherter Entscheidung: nur bei „granted“. Das gilt auch für die
 *   Schweiz — wer über den Fusszeilen-Link widerspricht, wird nicht getrackt.
 * - Ohne Entscheidung: nur ausserhalb von EWR/UK (dort gilt die
 *   Widerspruchslösung aus Abschnitt 5 der Datenschutzerklärung).
 *
 * @returns {boolean}
 */
export const isOpenAiTrackingAllowed = () => {
  if (typeof window === 'undefined' || !openAiPixelId) {
    return false;
  }
  const stored = readConsent();
  if (stored) {
    return stored === CONSENT_GRANTED;
  }
  return !isConsentRequiredRegion();
};

/**
 * Legt `window.oaiq` als Warteschlange an, falls `oaiq.min.js` noch nicht
 * geladen ist. Entspricht dem offiziellen OpenAI-Snippet, nur lesbar
 * ausgeschrieben: Aufrufe vor dem Laden landen in `q` und werden vom SDK beim
 * Start der Reihe nach nachgeholt.
 *
 * @returns {Function} window.oaiq
 */
const ensureOaiqQueue = () => {
  if (typeof window.oaiq !== 'function') {
    const oaiq = function oaiq() {
      oaiq.q.push(arguments);
    };
    oaiq.q = [];
    window.oaiq = oaiq;
  }
  return window.oaiq;
};

/**
 * Bindet das Pixel ein und meldet den ersten Seitenaufruf. Idempotent —
 * mehrfaches Aufrufen (z. B. nach nachträglicher Einwilligung) lädt das Skript
 * nur einmal.
 *
 * @returns {boolean} true, wenn das Pixel aktiv ist
 */
export const initOpenAiPixel = () => {
  if (!isOpenAiTrackingAllowed()) {
    return false;
  }
  if (document.getElementById(SCRIPT_ID)) {
    return true;
  }

  const oaiq = ensureOaiqQueue();
  oaiq('init', { pixelId: openAiPixelId });
  // Eine ausdrückliche Zustimmung auch dem SDK melden: Es merkt sie sich
  // (Cookie `__oaiq_consent`) und kann sie beim nächsten Laden wieder
  // einlesen. Ohne Entscheidung (Schweizer Widerspruchslösung) bleibt der
  // SDK-Standard `null` stehen — dann setzen wir bewusst kein Consent-Cookie.
  if (readConsent() === CONSENT_GRANTED) {
    oaiq('consent', true);
  }
  // Das SDK meldet beim Init KEINEN Seitenaufruf von selbst.
  oaiq('measure', 'page_viewed', { type: 'contents' });

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  // Kein crossOrigin: das offizielle Snippet setzt es nicht, und ein
  // CORS-Request würde das Skript blockieren, falls bzrcdn.openai.com die
  // Header nicht liefert.
  script.src = SCRIPT_SRC;
  document.head.appendChild(script);
  return true;
};

/**
 * Reaktion auf eine Entscheidung im Einwilligungs-Banner: Nach einer Zustimmung
 * wird das Pixel nachgeladen, nach einem Widerspruch ein bereits geladenes SDK
 * stummgeschaltet. `oaiq('consent', false)` verwirft dabei die Warteschlange und
 * löscht die Klick-ID — Reddit bietet dafür kein Gegenstück.
 *
 * @returns {void}
 */
export const applyOpenAiConsent = () => {
  if (typeof window === 'undefined') {
    return;
  }
  if (readConsent() === CONSENT_DENIED) {
    if (typeof window.oaiq === 'function') {
      window.oaiq('consent', false);
    }
    return;
  }
  initOpenAiPixel();
};

/**
 * Übersetzt die GA4-Parameter in das Schema des OpenAI-Pixels — strikt nur die
 * Felder, die der jeweilige Datenobjekt-Typ kennt. Ein einziges undokumentiertes
 * Feld lässt das SDK das ganze Ereignis verwerfen.
 *
 * @param {string} dataType 'contents' | 'customer_action' | 'plan_enrollment' | 'custom'
 * @param {Object} params GA4-Parameter
 * @returns {Object} eventProps für `oaiq('measure', …)`
 */
const toOpenAiProps = (dataType, params = {}) => {
  const props = { type: dataType };

  // Betrag: GA4 misst in Währungseinheiten (42.5 = CHF 42.50), OpenAI erwartet
  // eine GANZE Zahl in der kleinsten Einheit (4250 Rappen). Gegen das echte SDK
  // gemessen: `amount: 42.5` wird mit „eventProps.amount should be an integer"
  // KOMPLETT verworfen — und zwar still, denn runde Preise (500 → 500) kämen
  // durch und nur Beträge mit Rappen fielen weg. Der Faktor 100 gilt für CHF
  // (und jede andere Zwei-Nachkommastellen-Währung dieses Marktplatzes).
  //
  // `amount` ohne `currency` verwirft das SDK ebenfalls („currency is required
  // when eventProps.amount is present"), also reisen die beiden nur gemeinsam.
  const hasValue = typeof params.value === 'number' && !Number.isNaN(params.value);
  if (hasValue && params.currency) {
    props.amount = Math.round(params.value * 100);
    props.currency = params.currency;
  }

  if (params.listing_id) {
    if (dataType === 'plan_enrollment') {
      // Beim Abo ist das Inserat der gebuchte Plan.
      props.plan_id = params.listing_id;
    } else if (CONTENTS_DATA_TYPES.has(dataType)) {
      props.contents = [{ id: params.listing_id, quantity: 1 }];
    }
  }

  return props;
};

/**
 * Meldet ein semantisches GA4-Ereignis an OpenAI, sofern es zugeordnet ist.
 * No-Op ohne geladenes Pixel (fehlende ID, fehlende Einwilligung, SSR).
 *
 * @param {string} event GA4-Ereignisname, z. B. 'booking_request'
 * @param {Object} [params] GA4-Parameter
 * @returns {void}
 */
export const trackOpenAiEvent = (event, params = {}) => {
  if (typeof window === 'undefined' || typeof window.oaiq !== 'function') {
    return;
  }
  const mapped = OPENAI_EVENTS[event];
  if (!mapped) {
    return;
  }

  const isCustom = mapped === CUSTOM;
  const openAiEvent = isCustom ? 'custom' : mapped;
  const dataType = EVENT_DATA_TYPES[openAiEvent];

  const options = {};
  if (isCustom) {
    // Pflichtfeld für Custom-Ereignisse; ohne gültigen Namen wird verworfen.
    options.custom_event_name = event;
  }
  if (params.transaction_id) {
    // Dedupliziert Mehrfachmeldungen derselben Buchung (Reload der
    // Erfolgsseite, Wiederholungen nach Netzfehlern).
    options.event_id = params.transaction_id;
  }

  window.oaiq(
    'measure',
    openAiEvent,
    toOpenAiProps(dataType, params),
    Object.keys(options).length > 0 ? options : undefined
  );
};

/**
 * Seitenaufruf nach In-App-Navigation melden. Der erste Seitenaufruf kommt
 * bereits aus `initOpenAiPixel`.
 *
 * @returns {void}
 */
export const trackOpenAiPageView = () => {
  if (typeof window === 'undefined' || typeof window.oaiq !== 'function') {
    return;
  }
  window.oaiq('measure', 'page_viewed', { type: 'contents' });
};

// Nur für Tests: die Zuordnungstabellen sichtbar machen, ohne sie zum Teil der
// öffentlichen API zu machen.
export const __testables = { OPENAI_EVENTS, EVENT_DATA_TYPES, CUSTOM, toOpenAiProps };
